import { ButtonLink } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { Offer } from '@dash-lite/sdk';
import { Check } from 'lucide-react';
import { Alert, Badge, Card, CardBody } from '@/components/ui/primitives';
import { useSite } from '@/hooks/use-site';
import { describeOffer, formatDuration, formatMoney, headlinePrice } from '@/lib/format';
import { paths } from '@/lib/paths';

/**
 * `GET /site` et `GET /offer` renvoient un champ `purchasable` que le type
 * `Offer` du SDK n'expose pas encore. La documentation est claire à son
 * sujet : **n'affichez le bouton d'achat que s'il vaut `true`** — l'offre
 * n'est achetable qu'une fois synchronisée avec le compte Stripe du site,
 * pour l'environnement courant.
 *
 * En attendant qu'il rejoigne le type officiel, on le lit ici de façon
 * défensive : `undefined` est traité comme `true`, pour ne pas masquer le
 * bouton face à une version de l'API qui ne renverrait pas encore le champ.
 */
function isPurchasable(offer: Offer): boolean {
    return (offer as Offer & { purchasable?: boolean }).purchasable !== false;
}

/**
 * Carte de présentation de l'offre du site.
 *
 * **Un site n'a qu'une seule offre**, active en permanence : il n'y a pas de
 * catalogue à parcourir, ni d'identifiant d'offre à transporter dans le
 * tunnel. L'offre arrive avec `GET /site` — c'est pourquoi cette carte n'a
 * besoin d'aucun appel supplémentaire.
 *
 * Deux points d'attention :
 *
 * - `offer` vaut `null` tant que le site ne l'a pas configurée : la carte
 *   affiche alors un message explicite plutôt qu'un prix vide ;
 * - les trois types d'offre (`one_time`, `subscription`,
 *   `trial_then_subscription`) partagent la même intégration technique. Seule
 *   la formulation commerciale change, et elle est centralisée dans
 *   `describeOffer()`.
 *
 * L'attribut `data-dashlite="offer-checkout"` sur le bouton d'achat est requis
 * par le robot de vérification de mise en production : c'est par lui qu'il
 * trouve l'entrée du tunnel. Il est invisible pour vos visiteurs.
 */
export function OfferCard({
    /** Contexte d'achat conservé jusqu'au droit d'accès (voir `metadata`). */
    returnTo,
    className,
    highlighted = true,
}: {
    returnTo?: string;
    className?: string;
    highlighted?: boolean;
}) {
    const site = useSite();
    const offer = site.offer;

    if (!offer) {
        return (
            <Alert tone="caution" title="Aucune offre n'est configurée pour ce site" className={className}>
                <code className="font-mono text-xs">GET /site</code> a renvoyé <code className="font-mono text-xs">offer: null</code>. Le
                site n'a pas encore défini son offre dans la console Dash Lite.
            </Alert>
        );
    }

    const checkoutUrl = returnTo
        ? `${paths.checkout}?${new URLSearchParams({ entreprise: returnTo }).toString()}`
        : paths.checkout;

    return (
        <Card className={cn(highlighted && 'border-brand-200 ring-brand-100 ring-4', className)}>
            <CardBody className="space-y-6 p-7 sm:p-8">
                <div>
                    <div className="flex items-start justify-between gap-4">
                        <h3 className="text-ink font-sans text-lg font-semibold">{offer.name}</h3>
                        <OfferTypeBadge type={offer.type} />
                    </div>

                    {offer.description && <p className="text-ink-soft mt-2 text-sm leading-relaxed">{offer.description}</p>}
                </div>

                <div>
                    <p className="text-ink font-display text-5xl leading-none">{headlinePrice(offer)}</p>
                    <p className="text-ink-muted mt-2 text-sm">{describeOffer(offer)}</p>
                </div>

                <ul className="space-y-2.5">
                    {buildBenefits(offer).map((benefit) => (
                        <li key={benefit} className="text-ink-soft flex items-start gap-2.5 text-sm">
                            <Check className="text-positive mt-0.5 size-4 shrink-0" aria-hidden />
                            {benefit}
                        </li>
                    ))}
                </ul>

                {isPurchasable(offer) ? (
                    <>
                        <ButtonLink to={checkoutUrl} size="lg" className="w-full" data-dashlite="offer-checkout">
                            {offer.type === 'one_time' ? 'Accéder à la fiche complète' : "Souscrire à l'offre"}
                        </ButtonLink>

                        <p className="text-ink-muted text-center text-xs">
                            Paiement par carte, sur cette page. Aucune donnée bancaire ne transite par nos serveurs.
                        </p>
                    </>
                ) : (
                    <Alert tone="caution" title="Offre momentanément indisponible">
                        L'offre n'est pas encore synchronisée avec le compte Stripe du site pour cet environnement
                        (<code className="font-mono text-xs">purchasable: false</code>). Le bouton d'achat reste masqué
                        tant que c'est le cas.
                    </Alert>
                )}
            </CardBody>
        </Card>
    );
}

/**
 * Liste d'avantages construite à partir des seules données de l'offre.
 * Rien n'est promis qui ne soit vérifiable dans la réponse de l'API.
 */
function buildBenefits(offer: Offer): string[] {
    const benefits = [
        'Fiche d’information complète : identité, immatriculation, capital, activité, effectifs',
        'Dirigeants et juridiction compétente',
        'Données issues des registres officiels',
    ];

    if (offer.access_duration_hours) {
        benefits.push(`Accès pendant ${formatDuration(offer.access_duration_hours)} après le paiement`);
    }

    if (offer.type === 'subscription' || offer.type === 'trial_then_subscription') {
        benefits.push('Consultations illimitées pendant toute la durée de l’abonnement');
        benefits.push('Résiliation en ligne à tout moment, sans justification');
    }

    if (offer.type === 'trial_then_subscription' && offer.trial) {
        benefits.push(
            `Période d’essai à ${formatMoney(offer.trial.price)}${offer.trial.days ? ` pendant ${offer.trial.days} jours` : ''}`,
        );
    }

    return benefits;
}

function OfferTypeBadge({ type }: { type: Offer['type'] }) {
    switch (type) {
        case 'one_time':
            return <Badge tone="brand">Paiement unique</Badge>;
        case 'subscription':
            return <Badge tone="brand">Abonnement</Badge>;
        case 'trial_then_subscription':
            return <Badge tone="brand">Essai puis abonnement</Badge>;
    }
}
